<template>
  <v-card class="pa-6"> 
    <!-- {{ type }} -->
    <v-card-title class="text-h5 text-primary mb-4">
      Inscrição {{ type === "profissional" ? "Mestrado Profissional" : "Regular" }}
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid" lazy-validation>
        <!-- Overlay de carregamento -->
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64" color="primary" />
          <span class="ml-4 text-h6">Carregando dados...</span>
        </v-overlay>

        <!-- Detalhes do Projeto -->
        <div class="section-header mb-4">Detalhes do Projeto</div>
        <v-row dense> <!-- Título do Projeto -->
          <v-col cols="12" md="6">
            <v-text-field v-model="form.project_title" label="Título do Projeto*"
              :rules="[v => !!v || 'Campo obrigatório']" required :disabled="loading" />
          </v-col>

          <!-- Linha de Pesquisa -->
          <v-col cols="12" md="6">
            <v-select v-model="form.research_line" :items="researchLines" item-title="name" item-value="id"
              label="Linha de Pesquisa*" :rules="[(v) => !!v || 'Campo obrigatório']" :disabled="loading"
              @update:modelValue="filterResearchGroups" />
          </v-col>

          <!-- Eixo Temático (profissional) -->
          <v-col cols="12" md="6" v-if="type === 'profissional'">
            <v-select v-model="form.thematic_axis" :items="filteredAxes" item-title="name" item-value="id"
              label="Eixo Temático*" :rules="[(v) => !!v || 'Campo obrigatório']"
              :disabled="loading || !form.research_line" />
          </v-col>

          <!-- Grupo de Pesquisa (regular) -->
          <v-col cols="12" md="6" v-if="type === 'regular'">
            <v-select v-model="form.research_group" :items="filteredGroups" item-title="name" item-value="id"
              label="Grupo de Pesquisa*" :rules="[(v) => !!v || 'Campo obrigatório']"
              :disabled="loading || !form.research_line" />
          </v-col>

          <!-- Nível do Curso (apenas regular) -->
          <v-col cols="12" md="6" v-if="type === 'regular'">
            <v-select v-model="form.degree_level" :items="['Mestrado', 'Doutorado']" label="Nível do Curso*"
              :rules="[(v) => !!v || 'Campo obrigatório']" required @update:modelValue="filterDocuments"
              :disabled="loading" />
          </v-col>
        </v-row>

        <!-- Documentos Obrigatórios -->
        <div class="section-header mb-4">Documentação Obrigatória</div>
        <v-row dense>
          <v-col cols="12" v-for="doc in filteredRequiredDocs" :key="doc.id">
            <v-file-input
              v-model="form.documentsMap[doc.id]"
              :label="getDocumentLabel(doc.id, doc.name)"
              accept="application/pdf"
              :rules="getFileRules(doc)"
              :required="doc.required"
              :disabled="loading"
              :error-messages="backendErrors[`documents.${doc.name}`]"
              @update:modelValue="onDocumentChanged(doc)"
            >
              <template #append>
                <v-btn
                  v-if="form.existingDocuments[doc.id]"
                  icon
                  size="small"
                  @click="baixarDocumento('documents', form.existingDocuments[doc.id])"
                >
                  <v-icon color="primary">mdi-download</v-icon>
                </v-btn>
              </template>
            </v-file-input>
          </v-col>
        </v-row>

        <!-- Cotas -->
        <div class="section-header mb-4">Participação no Programa de Cotas</div>

        <v-select v-model="form.quota_participation" :items="quotaOptions" item-title="text" item-value="value" label="Participação no programa de cotas*"
          :rules="[(v) => !!v || 'Campo obrigatório']" required :disabled="loading" />

        <!-- BLOCO EXIBIDO QUANDO NÃO FOR "NÃO" -->
        <div v-if="form.quota_participation !== '0'" class="mt-4">

          <!-- AUTORIZAÇÃO (SOMENTE PRETOS, NEGROS E PARDOS) -->
          <div v-if="form.quota_participation === '3'" class="mb-4">

            <div class="text-subtitle-1 font-weight-bold mb-2">
              Autorização para uso da imagem (fotografia)*
            </div>

            <v-radio-group v-model="form.image_authorization" :rules="[(v) => !!v || 'Campo obrigatório']" required>
              <v-radio value="sim">
                <template #label>
                  <span>
                    Autorizo o uso, para a primeira etapa da averiguação, da autodeclaração das imagens coletadas no dia
                    da prova escrita. Estou ciente que, caso a análise seja inconclusiva, participarei da etapa
                    complementar de averiguação on-line, conforme a
                    <a href="https://www.pg.unicamp.br/norma/21795/1" target="_blank" rel="noopener" style="text-decoration: none; color: #1976d2;">
                      Resolução GR nº 74/2020
                    </a>.*
                  </span>
                </template>
              </v-radio>

              <v-radio value="nao">
                <template #label>
                  <span>
                    Não autorizo o uso das imagens coletadas no dia da prova escrita para avaliação do fenótipo. Estou
                    ciente que participarei da etapa de averiguação on-line, conforme a
                    <a href="https://www.pg.unicamp.br/norma/21795/1" target="_blank" rel="noopener" style="text-decoration: none; color: #1976d2;" >
                      Resolução GR nº 74/2020
                    </a>.*
                  </span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <!-- BLOCO DOCUMENTOS -->
          <v-card class="pa-4 mt-4" outlined>
            <div class="text-h6 mb-3">Documentos</div>

            <p>
              Documentos para candidatos optantes pelo sistema de cotas.
            </p>
            <p>
              As mesmas recomendações fornecidas anteriormente para os outros documentos aplicam-se a estes.
            </p>
            <p>
              Por favor, verifique no 
              <a href="https://www.fe.unicamp.br/sites/www.fe.unicamp.br/files/documents/2024/06/Edital_MP_2025_final.pdf"
                target="_blank" rel="noopener" class="text-primary" style="text-decoration: none;">
                Edital
              </a> as instruções para estes documentos. Além disso, certifique-se de assiná-los quando necessário.
            </p>

            <div class="font-weight-bold mt-4 mb-2">
              Declaração de optante por cotas
            </div>

            <v-row dense>
              <v-col cols="12" v-for="doc in quotaDocs" :key="doc.id">
                <v-file-input v-model="form.documentsMap[doc.id]" :label="getDocumentLabel(doc.id, doc.name)"
                  accept="application/pdf" :rules="getFileRules(doc)" required :disabled="loading"
                  :error-messages="backendErrors[`documents.${doc.name}`]"
                  @update:modelValue="delete backendErrors[`documents.${doc.name}`]">
                  <template #append>
                    <v-btn
                      v-if="form.existingDocuments[doc.id]"
                      icon
                      size="small"
                      @click="baixarDocumento('documents', form.existingDocuments[doc.id])"
                    >
                      <v-icon color="primary">mdi-download</v-icon>
                    </v-btn>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
          </v-card>

        </div>

        <!-- Necessidades Especiais -->
        <div class="section-header mb-4">Você possui alguma necessidade especial para a realização de alguma das fases do processo seletivo?</div>
        <v-radio-group v-model="form.special_needs" row :rules="[(v) => !!v || 'Selecione uma opção']" required>
          <v-radio label="Não" value="Não" />
          <v-radio label="Sim" value="Sim" />
        </v-radio-group>

        <v-textarea v-if="form.special_needs === 'Sim'" v-model="form.special_needs_details"
          label="Por favor, especifique:" :rules="[(v) => !!v || 'Campo obrigatório']" rows="4" required />

        <!-- Publicações -->
         <!-- Sob solicitação da coordenação da pós foram retirados os campos de publicação para o usuário preencher -->
        <div v-show="false">
          <div class="section-header mb-4">Publicações (Opcional)</div>
          <v-row dense>
            <v-col cols="12" v-for="i in 3" :key="i">
              <v-file-input
                accept="application/pdf"
                v-model="form.publications[i - 1]"
                :label="getPublicationLabel(i - 1)"
                :rules="getFileRules(i)"
                clearable
                @click:clear="removePublication(i - 1)"
              >
                <template #append>
                  <v-btn
                    v-if="form.existingPublications[i - 1]"
                    icon
                    size="small"
                    @click="baixarDocumento('publications', form.existingPublications[i - 1])"
                  >
                    <v-icon color="primary">mdi-download</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="form.existingPublications[i - 1]"
                    icon
                    size="small"
                    color="red"
                    @click.prevent="removeExistingPublication(i - 1)"
                  >
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
        </div>

        <!-- Termo de Responsabilidade -->
        <div class="section-header mb-3 mt-6">Termo de Responsabilidade</div>
        <v-checkbox
          v-model="term_agreement"
          density="compact"
          hide-details="auto"
          class="mb-2"
        >
          <template #label>
            <span>Declaro que estou de acordo com os termos do edital e estou ciente dos critérios de seleção apresentados no site da pós-graduação.</span>
          </template>
        </v-checkbox>
        <v-checkbox
          v-model="term_responsibility"
          density="compact"
          hide-details="auto"
          class="mb-4"
        >
          <template #label>
            <span>Declaro que é de minha inteira responsabilidade a conferência das informações e dos arquivos, e que posso ser desqualificado por fornecer informações ou documentos incorretos.</span>
          </template>
        </v-checkbox>

        <!-- Botões -->
        <v-row justify="end" class="mt-4" no-gutters>
          <v-col cols="auto" class="d-flex pa-0" style="gap: 8px">
            <v-btn color="secondary" @click="$emit('back')">Voltar</v-btn>
            <v-btn
              color="success"
              :disabled="!term_agreement || !term_responsibility || submitting || processStatus !='started'"
              @click="saveForm(true)"
            >Finalizar Inscrição
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>

  <v-dialog
    v-model="showPdfHelp"
    max-width="700"
  >

    <v-card class="pa-6"> 
      <v-card-title class="v-text-h5 bg-error mb-4 text-center px-6">
        O arquivo PDF selecionado não pode ser processado.
      </v-card-title>
      <v-card-subtitle class="text-h5 text-primary mb-4">
        Como corrigir um arquivo PDF
      </v-card-subtitle>

      <v-card-text>
        <p>O sistema aceita apenas arquivos em formato PDF, de no máximo 5MB.</p>
        <p>Alguns arquivos PDF emitidos por determinados sistemas podem não ser reconhecidos durante o envio, mesmo abrindo normalmente em um leitor de PDF.</p>

        <p class="mt-4">
          Para corrigir o arquivo:
        </p>

        <ol class="pl-6">
          <li>Abra o arquivo em um leitor de PDF, como Adobe Acrobat Reader, Foxit Reader ou outro programa compatível.</li>
          <li>Escolha a opção <strong>Imprimir</strong>.</li>
          <li>Na lista de impressoras, selecione <strong>Microsoft Print to PDF</strong> (Windows) ou <strong>Salvar como PDF</strong>, se disponível.</li>
          <li>Clique em <strong>Imprimir</strong> ou <strong>Salvar</strong>.</li>
          <li>Grave o novo arquivo com outro nome para preservar o original.</li>
          <li>Retorne ao sistema e envie o novo arquivo PDF.</li>
          <li>Caso o erro persista, entre em contato com ticfe@unicamp.br.</li>
        </ol>

        <v-alert
          type="info"
          variant="tonal"
          class="mt-4"
        >
          Esse procedimento apenas recria o arquivo PDF. O conteúdo do documento permanece o mesmo.
        </v-alert>
      </v-card-text>

      <v-card-actions class="pr-6">
        <v-spacer />
        <v-btn class="bg-primary"
          @click="showPdfHelp = false"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import axios from "axios";
import keycloak from "../../plugins/keycloak";
import { nextTick } from "vue";
import { downloadFile } from '@/utils/fileDownload';
import { getRegistrationStatus } from '@/utils/registration';

export default {
  name: "ApplicationFormRegular",
  props: ["processId", "type"],
  emits: ['back', 'next'],

  data() {
    return {
      loading: true,
      valid: false,
      submitting: false,
      backendErrors: {},
      term_agreement: false,
      term_responsibility: false,
      processStatus: "",

      form: {
        id: null,
        user_id: null,
        process_id: this.processId,
        degree_level: "",
        research_line: "",
        research_group: "",
        thematic_axis: "",
        project_title: "",
        documentsMap: {},
        existingDocuments: {},
        quota_participation: "0",
        image_authorization: "",
        special_needs: "Não",
        special_needs_details: "",
        publications: [null, null, null],
        existingPublications: [],
      },

      researchLines: [],
      researchGroups: [],
      filteredGroups: [],
      filteredAxes: [],

      requiredDocs: [],
      filteredRequiredDocs: [],

      quotaDocs: [],
      // Campos "value" de acordo com os valores indicados no Drupal (Posgrad)
      quotaOptions: [
        {text:"Não", value:"0"},
        {text:"PCD", value:"1"},
        {text:"Indígena", value:"2"},
        {text:"Pretos (Negros e Pardos)", value:"3"},
        {text:"Trans", value:"4"}],
      showPdfHelp: false,
      allow_masters_degree: false,
      allow_doctorate: false,
    };
  },
  async created() {
    try {
      // Carrega dados base do processo
      await this.loadProcessData();
      await this.loadVacancies();

      // Se já tiver nível selecionado (caso venha de inscrição existente depois, chamamos de novo lá)
      this.filterDocuments();

      const uuid = keycloak?.tokenParsed?.sub;
      if (uuid) {
        this.form.user_id = await this.getUserIdByUuid(uuid);
        await this.loadExistingApplication();
      }
    } finally {
      this.loading = false;
    }
  },
  computed: {
    researchGroupLabelComputed() {
      const total =
        this.type === 'regular'
          ? this.filteredGroups.length
          : this.filteredAxes.length;

      return total === 1
        ? this.processConfig.research_group_label
        : this.processConfig.research_group_label_plural;
    }
  },
  methods: {
    async loadProcessData() {
      const actives = this.$store.state.activeProcesses;
      const process = actives[this.processId];
      if (!process) return;
      this.processData = process;
      this.processStatus = getRegistrationStatus(this.processData.phases);

      const appForm = process.application_form;

      // =====================
      // CONFIGURAÇÕES
      // =====================
      this.processConfig = {
        allow_masters_degree: appForm.allow_masters_degree,
        allow_doctorate: appForm.allow_doctorate,
        has_quotas: process.has_quotas,
        research_group_label: process.research_group_label,
        research_group_label_plural: process.research_group_label_plural
      };

      // =====================
      // NÍVEL DO CURSO
      // =====================
      this.degreeOptions = [];
      if (appForm.allow_masters_degree) {
        this.allow_masters_degree = true;
        this.degreeOptions.push("Mestrado");
      }
      if (appForm.allow_doctorate) {
        this.allow_doctorate = true;
        this.degreeOptions.push("Doutorado");
      }
      if (this.degreeOptions.length === 1) {
        this.form.degree_level = this.degreeOptions[0];
      }

      // =====================
      // DOCUMENTOS
      // =====================
      const docs = appForm.documents || [];

      this.requiredDocs = [];
      this.quotaDocs = [];

      docs.forEach((doc, index) => {
        const base = {
          dtype_id: doc.dtype_id,
          id: `doc_${index}`,
          name: doc.name,
          required: doc.required === "1",
          req_masters: doc.req_masters === "1",
          req_doctorate: doc.req_doctorate === "1",
          req_quotas: doc.req_quotas === "1"
        };

        if (base.req_quotas) {
          base.id = `quota_doc_${this.quotaDocs.length}`;
          this.quotaDocs.push(base);
        } else {
          this.requiredDocs.push(base);
        }
      });
    },

    // =====================
    // LINHAS / GRUPOS / VAGAS
    // =====================
    async loadVacancies() {
      const url = `${this.$APIPATH}/processes/${this.processId}/vacancies`;
      let res;
      try {
        res = await axios.get(url, { skipAuth: true });
      } catch (err) {
        console.error("Erro ao carregar vagas:", err);
        this.$toast.error("Erro ao carregar vagas do processo. Tente recarregar a página.");
        return;
      }
      const vacancies = res.data?.data || [];

      const lineMap = {};
      const groupArr = [];

      vacancies.forEach((v) => {
        const line = v.research_line;
        const group = v.research_group;

        if (line?.id && !lineMap[line.id]) {
          lineMap[line.id] = { id: Number(line.id), name: line.name };
        }

        if (group?.id && line?.id) {
          groupArr.push({
            id: Number(group.id),
            name: group.name,
            research_line_id: Number(line.id),
          });
        }
      });

      this.researchLines = Object.values(lineMap);
      this.researchGroups = groupArr;

      this.filteredGroups = [];
      this.filteredAxes = [];
    },

    filterResearchGroups() {
      const selectedLine = Number(this.form.research_line);

      this.filteredGroups = this.researchGroups.filter(
        (g) => g.research_line_id === selectedLine
      );

      // no seu modelo atual, “eixo temático” usa a mesma lista do grupo
      this.filteredAxes = [...this.filteredGroups];

      if (this.filteredGroups.length === 1) this.form.research_group = this.filteredGroups[0].id;
      if (this.filteredAxes.length === 1) this.form.thematic_axis = this.filteredAxes[0].id;
    },

    // =====================
    // USUÁRIO
    // =====================
    async getUserIdByUuid(uuid) {
      try {
        const res = await axios.get(`${this.$PROFPATH}/profiles`, {
          params: {
            filters: {
              kc_uuid: uuid,
            },
          },
        });
        return res.data?.data?.[0]?.id ?? null;
      } catch {
        return null;
      }
    },

    filterDocuments() {
      const isRegular = this.type === "regular";
      const level = this.form.degree_level;

      this.filteredRequiredDocs = this.requiredDocs.filter((doc) => {
        if (!isRegular) return doc.req_masters; // mantém sua lógica original
        if (level === "Mestrado") return doc.req_masters;
        if (level === "Doutorado") return doc.req_doctorate;
        return true;
      });
    },

    getDocumentLabel(id, name) {
      return this.form.existingDocuments[id]
        ? `Clique aqui para alterar ${name}`
        : name;
    },

    getPublicationLabel(i) {
      return this.form.existingPublications[i]
        ? `Clique aqui para alterar Publicação ${i + 1}`
        : `Publicação ${i + 1}`;
    },

    removePublication(i) {
      this.form.publications[i] = null;
    },

    removeExistingPublication(i) {
      this.form.existingPublications.splice(i, 1);
    },

    // =====================
    // VALIDAÇÃO
    // =====================
    validateForm() {
      if (!this.$refs.form?.validate()) return false;

      for (const doc of this.filteredRequiredDocs) {
        if (
          doc.required &&
          !this.form.documentsMap[doc.id] &&
          !this.form.existingDocuments[doc.id]
        )
          return false;
      }

      if (this.form.quota_participation !== "0") {
        for (const doc of this.quotaDocs) {
          if (!this.form.documentsMap[doc.id] && !this.form.existingDocuments[doc.id])
            return false;
        }
      }

      return true;
    },


    // =====================
    // SALVAR FORM
    // =====================
    async saveForm(shouldNext) {
      if (!this.validateForm()) return;
      await this.$refs.form.validate();
      if (!this.valid) return;

      this.submitting = true;
      this.backendErrors = {};
      try {
        const formData = new FormData();
        if (!this.form.degree_level) {
          this.form.degree_level = this.degreeOptions[0];
        }
        if (this.form.thematic_axis && !this.form.research_group) {
          this.form.research_group = this.form.thematic_axis;
        }

        // Campos simples
        const simpleFields = [
          "user_id",
          "process_id",
          "degree_level",
          "research_line",
          "research_group",
          "thematic_axis",
          "project_title",
        ];
        simpleFields.forEach((f) => formData.append(f, this.form[f]));

        // Booleans
        formData.append("has_quotas", this.form.quota_participation !== "0" ? 1 : 0);
        formData.append("has_special_needs", this.form.special_needs === "Sim" ? 1 : 0);
        formData.append("term_agreement", this.term_agreement ? 1 : 0);
        formData.append("term_responsibility", this.term_responsibility ? 1 : 0);

        if (this.form.quota_participation)
          formData.append("quota_participation", this.form.quota_participation);
        if (this.form.image_authorization)
          formData.append("image_authorization", this.form.image_authorization);
        if (this.form.special_needs_details)
          formData.append("special_needs_description", this.form.special_needs_details);

        // Documentos
        Object.entries(this.form.documentsMap).forEach(([key, file], i) => {
          const docName = this.getDocumentName(key)
          const arquivoOriginal = file
          if (arquivoOriginal) {
            const dtype = this.getFileDType(key);
            const newFile = new File([arquivoOriginal], `${docName}.pdf`, { type: arquivoOriginal.type })
            formData.append(`documents[${i}][file]`, newFile);
            formData.append(`documents[${i}][dtype]`, dtype);
            formData.append(`documents[${i}][docName]`, docName);
          }
        })

        // Publicações
        for (let i = 0; i < this.form.publications.length; i++) {
          if (this.form.publications[i]) {
            const arquivoOriginal = this.form.publications[i]
            if (arquivoOriginal) {
              const arquivoRenomeado = new File([arquivoOriginal], `Publicacao_${i+1}.pdf`, { type: arquivoOriginal.type })
              this.form.publications[i] = arquivoRenomeado
            }
          }
        }
        this.form.publications.forEach((file) => {
          if (file instanceof File) formData.append("publications[]", file);
        });
        // Publicações existentes
        this.form.existingPublications.forEach((path) => {
          formData.append("existing_publications[]", path);
        });
       const url = this.form.id
          ? `${this.$BACKPATH}/regular-applications/${this.form.id}?_method=PUT`
          : `${this.$BACKPATH}/regular-applications`;

        const res = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.status >= 400) {
          this.backendErrors = res.data.errors || {};
          this.$toast.error(`Erro ao salvar inscrição: ${res.data.message}`);
        } else {
          this.$toast.success("Inscrição enviada com sucesso!");
          // Armazena a inscrição salva no localStorage para referência na Home
          const cacheKey = `regular-application-${this.form.user_id}-${this.form.process_id}`;
          localStorage.setItem(cacheKey, JSON.stringify({ user_id: this.form.user_id, process_id: this.form.process_id }));
          if (shouldNext) this.$emit("next");
        }
      } catch (err) {
        console.error(err);
        const message =
            err?.response?.data?.message ||
            err?.message ||
            String(err);
        this.$toast.error(message);
      } finally {
        this.submitting = false;
      }
    },

    // =====================
    // CARREGA INSCRIÇÃO EXISTENTE
    // =====================
    async loadExistingApplication() {
      if (!this.form.user_id || !this.form.process_id) return;
      try {
        const res = await axios.get(`${this.$BACKPATH}/regular-applications`, {
          params: {
            "filters[user_id]": this.form.user_id,
            "filters[process_id]": this.form.process_id,
          },
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });

        const app = res.data?.data?.[0];
        if (!app) return;

        this.form.id = app.id;
        this.form.project_title = app.project_title ?? "";
        this.form.degree_level = app.degree_level ?? "";
        this.form.quota_participation = app.quota_participation ?? "0";
        this.form.image_authorization = app.image_authorization ?? "";
        this.form.special_needs = app.has_special_needs ? "Sim" : "Não";
        this.form.special_needs_details = app.special_needs_description ?? "";
        this.term_agreement = !!app.term_agreement;
        this.term_responsibility = !!app.term_responsibility;

        // Linha de pesquisa
        const researchLineId = Number(app.research_line?.id ?? app.research_line);
        if (this.researchLines.some((l) => l.id === researchLineId)) {
          this.form.research_line = researchLineId;

          await nextTick();
          this.filterResearchGroups();

          // Grupo
          const groupId = Number(app.research_group?.id ?? app.research_group);
          this.form.research_group = this.filteredGroups.some((g) => g.id === groupId)
            ? groupId
            : "";

          // Eixo
          const axisId = Number(app.thematic_axis?.id ?? app.thematic_axis);
          this.form.thematic_axis = this.filteredAxes.some((a) => a.id === axisId)
            ? axisId
            : "";
        }

        // Documentos existentes
        this.filterDocuments();
        this.form.existingDocuments = {};
        [...this.filteredRequiredDocs, ...this.quotaDocs].forEach((doc, index) => {
          const file = app.documents?.[index];
          if (file) this.form.existingDocuments[doc.id] = file.path;
        });

        // Publicações existentes
        this.form.existingPublications = [];
        for (let i=0; i<app.publications.length; i++) {
          this.form.existingPublications.push(app.publications[i].path);
        }
      } catch (err) {
        console.error("Erro ao carregar inscrição existente:", err);
        this.$toast.error("Erro ao carregar dados da inscrição. Tente recarregar a página.");
      }
    },
    getFileRules(doc) {
      const maxSize = doc.dtype_id === "266" ? 80 * 1024 * 1024 : 5 * 1024 * 1024;
      const allowedTypes = [
        'application/pdf',
      ]
      return [
        // Obrigatório
        (v) => {
          if (!doc.required) return true
          if (this.form.existingDocuments[doc.id]) return true
          return (v && v.length > 0) || 'Arquivo obrigatório'
        },
        // Tamanho
        (v) => {
          if (!v) return true
          return v[0] ? v[0].size <= maxSize || 'Arquivo com tamanho superior ao estipulado em edital' : true
        },
        // Tipo
        (v) => {
          if (!v) return true
          return v[0] ? allowedTypes.includes(v[0].type) || 'Tipo de arquivo inválido' : true
        }
      ]
    },
    getDocumentName(chave) {
      for (let i=0; i<this.requiredDocs.length; i++) {
        if (this.requiredDocs[i].id === chave) {
          return this.requiredDocs[i].name;
        }
      }
      for (let i=0; i<this.quotaDocs.length; i++) {
        if (this.quotaDocs[i].id === chave) {
          return this.quotaDocs[i].name;
        }
      }
      return null;
    },
    getBackendErrorMessage(err) {
      const data = err.response?.data;
      if (!data) return "Erro de conexão. Verifique sua internet e tente novamente.";
      if (data.errors) {
        const messages = Object.values(data.errors).flat();
        return messages.join(" | ");
      }
      return data.message || "Erro inesperado. Tente novamente.";
    },

    baixarDocumento(type, path) {
      downloadFile(this.$BACKPATH, type, this.form.id, this.form.user_id, this.form.process_id, path)
    },
    async onDocumentChanged(doc) {
        await this.$nextTick();
        this.validateDocument(doc);
    },
    validateFile(file) {
        if (!file) {
            return true;
        }
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['application/pdf'];
        if (file.size > maxSize) {
            return 'Arquivo deve ter no máximo 5MB';
        }
        if (!allowedTypes.includes(file.type)) {
            return 'Tipo de arquivo inválido';
        }
        return true;
    },
    async validateDocument(doc) {
        delete this.backendErrors[`documents.${doc.name}`];
        const file = this.form.documentsMap[doc.id];
        if (!file) {
            return;
        }
        const validation = this.validateFile(file);
        if (validation !== true) {
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', this.form.user_id);
        formData.append('process_id', this.form.process_id);
        try {
            const response = await axios.post(
              `${this.$BACKPATH}/regular-applications/documents`,
              formData,
              {headers: {'Content-Type': 'multipart/form-data'}}
            );
            if (response.status === 422) {
                const msg = response.data.message;
                this.backendErrors[`documents.${doc.name}`] = msg;
                this.form.documentsMap[doc.id] = null;
                this.$toast.error(msg);
                this.showPdfHelp = true;
                return;
            }
          } catch (error) {
            this.backendErrors[`documents.${doc.name}`] =
                error.response?.data?.message ??
                'O arquivo não pôde ser validado.';
            this.form.documentsMap[doc.id] = null;
        }
    },
    getFileDType(key) {
      for (let i=0; i<this.filteredRequiredDocs.length; i++) {
        if (this.filteredRequiredDocs[i].id === key) {
          return this.filteredRequiredDocs[i].dtype_id;
        }
      }
      for (let i=0; i<this.quotaDocs.length; i++) {
        if (this.quotaDocs[i].id === key) {
          return this.quotaDocs[i].dtype_id;
        }
      }
      return null;
    }
  }
};
</script>

<style scoped>
.section-header {
  background: #e3f2fd;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: bold;
  color: #1976d2;
}
</style>
